import { useSuspenseQuery } from "@tanstack/react-query";
import { PageHero, Section } from "@/components/site/section";
import { FixtureCard, NewsCard, StatTile } from "@/components/site/cards";
import { fixturesQuery, newsByCategoryQuery, squadQuery } from "@/lib/site-queries";

export function AgeGroupPage({
  scope,
  title,
  eyebrow,
}: {
  scope: "u20" | "u17" | "u15";
  title: string;
  eyebrow: string;
}) {
  const { data: news } = useSuspenseQuery(newsByCategoryQuery("national_team", 4));
  const { data: fixtures } = useSuspenseQuery(fixturesQuery(scope, 6));
  const { data: squad } = useSuspenseQuery(squadQuery(scope));
  const totalCaps = squad.reduce((acc, p) => acc + (p.caps ?? 0), 0);
  const totalGoals = squad.reduce((acc, p) => acc + (p.goals ?? 0), 0);

  return (
    <>
      <PageHero eyebrow={eyebrow} title={title} />
      <Section id="news" eyebrow={eyebrow} title="News">
        {news.length ? (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {news.map((a) => <NewsCard key={a.id} article={a} />)}
          </div>
        ) : <p className="text-sm text-muted-foreground">No stories yet.</p>}
      </Section>
      <Section id="fixtures" eyebrow={eyebrow} title="Fixtures">
        {fixtures.length ? (
          <div className="grid gap-5 md:grid-cols-2">
            {fixtures.map((f) => <FixtureCard key={f.id} fixture={f} />)}
          </div>
        ) : <p className="text-sm text-muted-foreground">No fixtures scheduled.</p>}
      </Section>
      <Section id="squad" eyebrow={eyebrow} title="Squad">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {squad.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-lg border border-border bg-card p-4">
              <div>
                <p className="font-display font-bold text-navy">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.position} · {p.club}</p>
              </div>
              <span className="font-display text-2xl font-bold text-primary">{p.shirt_number}</span>
            </div>
          ))}
        </div>
      </Section>
      <Section id="statistics" eyebrow={eyebrow} title="Statistics">
        <div className="grid gap-4 md:grid-cols-3">
          <StatTile label="Players" value={squad.length} />
          <StatTile label="Combined caps" value={totalCaps} />
          <StatTile label="Combined goals" value={totalGoals} />
        </div>
      </Section>
    </>
  );
}
